"use client";

import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import equal from "fast-deep-equal";
import { Title } from "../title";

import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { checkValidity, parseValue, systemFields } from "@/lib/utils";
import { ItemsToConnect, ItemsToDisconnect, ItemsToSet } from "@/services/entity-instance.service";
import { DataTypeEnum } from "@/services/entity.service";
import { toast } from "sonner";
import { ConnectOrDisconnectEntityRelation } from "./connect-or-disconnect-entity-relation";
import { useEntitySchemaByName } from "@/hooks/schema/use-entity-schema-by-name";
import {
  StackedSheetDescription,
  StackedSheetHeader,
  StackedSheetTitle,
} from "../ui/stacked-sheet";
import { Dropzone } from "../dropzone";
import { useAnalytics } from "@/hooks/use-analytics";
import { InputByFieldType } from "../ui/input-by-field-type";
import { EntityFieldSelectBase } from "../entity-field-select-base";
import { Globe, LoaderCircle, Check, CircleAlert, EyeOff, Lock } from "lucide-react";
import { useEntityInstanceById } from "@/hooks/use-entity-instance-by-id";
import { UpdateEntityInstanceProps } from "./create-or-update-entity-instance";
import { useLanguages } from "@/hooks/use-languages";
import { EntityInstanceLanguageSelect } from "./entity-instance-language-select";
import { useEntityViewList } from "@/hooks/use-entity-view";
import { isDefaultView } from "@/hooks/use-dashboard-view";
import { convertEntityViewConditionToFilter } from "@/components/entity/entity-views/edit-entity-view";
import { QueryCondition } from "@okeano-gmbh/pylo-utils";
import { Field } from "@/services/entity-view.service";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OptionType } from "./connect-or-disconect-relations/relation-instance-select";
import { useMyAros } from "@/hooks/schema/use-my-aros";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEntityViewComponents } from "@/hooks/use-entity-view-components";
import { buildFileAcceptConfig, cleanupEntityName } from "@repo/utils/utils";
import { validateField } from "@/lib/utils";
import {
  EntityViewComponentMapping,
  toComponentTemplates,
} from "@/services/entity-view-components.service";
import { useEntityByName } from "@/hooks/schema/use-entity-by-name";
import { useEntityStore } from "@/stores/entity.store";
import {
  useEntityPermissionsMap,
  useFieldPermissionsForEntityId,
  isFieldGranted,
} from "@/hooks/schema/use-my-permissions";
import { usePermissionsStore } from "@/stores/permissions-store";
import { EntityField, EntityRelated } from "@/services/entity.service";
import { useFlowTriggerNotice } from "@/hooks/use-flow-by-trigger";
import { FlowTriggerHint, FlowTriggerSwitch } from "@/components/flows/flow-trigger-notice";
import { useFieldNameStore } from "@/stores/entity-instance-field-store";
import { EntityInstanceHistoryBadge } from "./entity-instance-history-badge";
import { EntityInstanceActivityFeed } from "./entity-instance-activity-feed";

export const CreateOrUpdateEntityInstanceContent: React.FC<UpdateEntityInstanceProps> = ({
  entityName,
  entityInstanceId,
  entityFromListCache,
  initialValues,
  initialRelations,
  isOpen = false,
  onOpenChanges,
  onSave,
  fieldsToHide = new Set(),
  permissions,
  isBottomLevel,
}) => {
  // const queryClient = useQueryClient();
  const isCreate = !entityInstanceId;
  const { capture } = useAnalytics();

  const [triggerFlows, setTriggerFlows] = useState(true);

  const {
    getEntityByName: getEntityByNameFromStore,
    getFieldOrRelationByPath,
    getEntityByName,
    getAllRelations,
  } = useEntityStore();

  const { entitySchemaWithRelationsCreateInputTypes } = useEntitySchemaByName(entityName);

  const { flows: relatedFlows } = useFlowTriggerNotice({
    entityName,
    operation: isCreate ? "create" : "update",
  });

  const entityFromStore = getEntityByNameFromStore(entityName);
  const { entityPermissionsMap } = useEntityPermissionsMap();
  const { entityViews } = useEntityViewList(
    entityFromStore?.id ?? entitySchemaWithRelationsCreateInputTypes?.id ?? "",
  );

  const entity = useEntityByName(entityName);
  // Use entity from store as primary source, fallback to schema hook
  const primaryEntity = entityFromStore ?? entity?.entity;

  const { selectedLanguage } = useLanguages();
  const myArosQuery = useMyAros();

  const hiddenFields = useMemo(() => {
    return new Set([...fieldsToHide, ...systemFields]);
  }, [fieldsToHide]);

  // Per-field read/write gating from the permission `field_paths` whitelist. Root users and
  // entities without restricting rows are unaffected (isFieldGranted returns true). A field the
  // user can't read comes back null from the API; one they can't write would make the whole upsert
  // throw — so both are surfaced and the input disabled.
  const fieldPermissionRows = useFieldPermissionsForEntityId(primaryEntity?.id ?? "");
  const hasRootPrivileges = usePermissionsStore((state) => state.hasRootPrivileges);

  const canReadField = useCallback(
    (fieldName: string) =>
      hasRootPrivileges || isFieldGranted(fieldPermissionRows, "read", fieldName),
    [hasRootPrivileges, fieldPermissionRows],
  );

  const canWriteField = useCallback(
    (fieldName: string) =>
      hasRootPrivileges ||
      isFieldGranted(fieldPermissionRows, isCreate ? "create" : "update", fieldName),
    [hasRootPrivileges, fieldPermissionRows, isCreate],
  );

  // Fields to strip from payload before sending to backend.
  // "id" must be kept — the backend uses it to identify update vs create.
  const fieldsToRemoveFromPayload = systemFields.filter((name) => name !== "id");

  // we cannot and don't want to update these fields; the id is needed for updating
  const removeSystemFields = useCallback(
    (data: object) => {
      // Get entity schema to identify relation fields
      const entity = getEntityByName(entityName);
      if (!entity) {
        // Fallback: just remove system fields if entity not found
        return Object.fromEntries(
          Object.entries(data).filter(([key]) => !fieldsToRemoveFromPayload.includes(key)),
        );
      }

      // Identify relation fields from entity schema
      const relationFieldNames = new Set<string>();

      // Add relation fields from all relations (merged and normalized)
      const allRelations = getAllRelations(entityName);
      allRelations?.forEach((relation) => {
        relationFieldNames.add(relation.field_name);
      });

      return Object.fromEntries(
        Object.entries(data).filter(
          ([key]) => !fieldsToRemoveFromPayload.includes(key) && !relationFieldNames.has(key),
        ),
      );
    },
    [getEntityByName, entityName, getAllRelations],
  );

  const fieldsToFetch = useMemo(() => {
    // TODO: if we have an active view, only fetch the fields, that are visible in the view

    const variantFields =
      primaryEntity?.entity_fields?.data
        .filter((field) => field.variant_default_entity_instance_id)
        .map((field) => `${field.name}_variants`) ?? [];

    const variantSubFields = ["value", "variant"];

    const variantFieldsWithSubFields = variantFields.map((field) =>
      variantSubFields.map((subField) => `${field}.${subField}`),
    );

    const nonVariantFields =
      primaryEntity?.entity_fields?.data
        .filter((field) => !field.variant_default_entity_instance_id)
        .map((field) => field.name) ?? [];

    return [...nonVariantFields, ...variantFieldsWithSubFields.flat()];
  }, [primaryEntity?.entity_fields?.data]);

  const {
    data: entityInstance,
    isFetching: isFetchingEntityInstance,
    isError: isEntityInstanceError,
  } = useEntityInstanceById({
    entityName: entityName,
    id: entityInstanceId ?? "",
    // placeholderInstance: entityFromListCache, // FIXME: we currently fetch the relation fields here too;
    fieldsToFetch: fieldsToFetch,
  });

  // Determine which entity view to use based on conditions
  const [selectedViewId, setSelectedViewId] = useState<string>("");
  // Tabs: track active section
  const [activeSectionTab, setActiveSectionTab] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [activityExpanded, setActivityExpanded] = useState(false);
  const activityAnchorId = entityInstanceId ? `entity-activity-${entityInstanceId}` : undefined;
  const formScrollRef = useRef<HTMLDivElement>(null);
  const openActivityFeed = useCallback(() => {
    setActivityExpanded(true);
    if (!activityAnchorId) return;
    requestAnimationFrame(() => {
      const container = formScrollRef.current;
      const anchor = document.getElementById(activityAnchorId);
      if (!container || !anchor) return;
      // Not scrollIntoView: it also scrolls the sheet's overflow-hidden ancestors, shifting the whole sheet.
      container.scrollTo({
        top:
          container.scrollTop +
          anchor.getBoundingClientRect().top -
          container.getBoundingClientRect().top,
        behavior: "smooth",
      });
    });
  }, [activityAnchorId]);

  // State to track field display changes in the current form session

  const { eilStates, updateEilState } = useFieldNameStore();

  const availableViews = useMemo(() => {
    if (!entityViews?.data || entityViews.data.length === 0) {
      return [];
    }

    // Get current user ARO ids
    const myAroIds = (myArosQuery.data?.data ?? []).map((aro) => aro.id);

    // If we have an entity instance, filter views by conditions and AROs
    if (entityInstance?.data) {
      const instance = entityInstance.data;

      return entityViews.data.filter((view) => {
        // ARO check: if view.pylo_aros is empty or undefined, everyone can see it
        const viewAroIds = view.pylo_aros?.map((aro) => aro.id) ?? [];

        if (viewAroIds.length === 0) {
          // If no AROs are connected, everyone can see it
          return true;
        }

        // Otherwise, check if user has any of the connected AROs
        if (!viewAroIds.some((id: string) => myAroIds.includes(id))) {
          return false;
        }

        if (!view.condition) {
          return true;
        }

        try {
          const parsedCondition = JSON.parse(view.condition) as QueryCondition;
          const filter = convertEntityViewConditionToFilter(parsedCondition);

          if (!filter?.query) {
            return true;
          }

          // Check if the entity instance matches the condition
          const matchesCondition = filter.query.every((queryItem) => {
            if (!queryItem.condition) return true;

            const { field, operator, value } = queryItem.condition;

            // @ts-expect-error because of the generalization
            const fieldValue = instance[field];

            switch (operator) {
              case "equal":
                return fieldValue == value;
              case "notEqual":
                return fieldValue != value;
              case "greaterThan":
                return value !== undefined && fieldValue > value;
              case "lessThan":
                return value !== undefined && fieldValue < value;
              case "greaterThanOrEqual":
                return value !== undefined && fieldValue >= value;
              case "lessThanOrEqual":
                return value !== undefined && fieldValue <= value;
              case "like":
              case "ilike":
                return (
                  value !== undefined &&
                  String(fieldValue).toLowerCase().includes(String(value).toLowerCase())
                );
              case "notLike":
              case "notiLike":
                return (
                  value !== undefined &&
                  !String(fieldValue).toLowerCase().includes(String(value).toLowerCase())
                );
              case "isNull":
                return fieldValue == null || fieldValue == undefined;
              case "isNotNull":
                return fieldValue != null && fieldValue != undefined;
              case "isEmpty":
                return (
                  fieldValue == null || fieldValue == undefined || String(fieldValue).trim() === ""
                );
              case "isNotEmpty":
                return (
                  fieldValue != null && fieldValue != undefined && String(fieldValue).trim() !== ""
                );
              case "isTrue":
                return fieldValue === true || fieldValue === "true";
              case "isFalse":
                return fieldValue === false || fieldValue === "false";
              default:
                return true;
            }
          });

          return matchesCondition;
        } catch (error) {
          console.error(`Failed to parse condition for view ${view.name}:`, error);
          return true;
        }
      });
    }

    // If no entity instance, filter only by AROs
    return entityViews.data.filter((view) => {
      const viewAroIds = view.pylo_aros?.map((aro) => aro.id) ?? [];

      // If no AROs are connected, everyone can see it
      if (viewAroIds.length === 0) {
        return true;
      }

      // Otherwise, check if user has any of the connected AROs
      if (!viewAroIds.some((id: string) => myAroIds.includes(id))) {
        return false;
      }
      return true;
    });
  }, [entityInstance?.data, entityViews.data, myArosQuery.data?.data]);

  const activeView = useMemo(() => {
    if (selectedViewId) {
      const selectedView = availableViews.find((v) => v.id === selectedViewId);
      if (selectedView) return selectedView;
    }
    return availableViews[0];
  }, [availableViews, selectedViewId]);

  const [entityInstanceValues, setEntityInstanceValues] = useState<
    | {
        [field: string]: unknown;
      }
    | undefined
  >({});

  // Snapshot of the values loaded into the form, used to diff which fields actually
  // changed so an update only sends the dirty ones.
  const initialValuesRef = useRef<Record<string, unknown>>({});

  const [itemsToConnect, setItemsToConnect] = useState<ItemsToConnect>({});
  const [itemsToDisconnect, setItemsToDisconnect] = useState<ItemsToDisconnect>({});
  const [itemsToSet, setItemsToSet] = useState<ItemsToSet>({});

  // form validation
  const [fieldValidity, setFieldValidity] = useState<Record<string, boolean>>({});

  const updateFieldValidity = useCallback((name: string, isValid: boolean) => {
    setFieldValidity((prev) => {
      const current = prev[name];
      if (current === isValid) return prev;
      return { ...prev, [name]: isValid };
    });
  }, []);

  const isFormValid = Object.values(fieldValidity).every(Boolean);
  const [shouldValidate, setShouldValidate] = useState(false);

  // Merge cached data with fetched data using schema-based field detection
  // Scalar fields: use cached data (updated values)
  // Relation fields: always use fetched data (complete structure)
  const mergedEntityInstance = useMemo(() => {
    if (!entityInstance?.data) return null;

    const cleanedEntityInstance = removeSystemFields(entityInstance.data);
    const variantFieldKeys = Object.keys(cleanedEntityInstance).filter((key) =>
      key.endsWith("_variants"),
    );
    // remove the data object from the variant fields and add suffic so we can directly set them again
    variantFieldKeys.forEach((key) => {
      const variantData = cleanedEntityInstance[key]?.data;
      // Ensure variant fields are always arrays
      cleanedEntityInstance[key] = Array.isArray(variantData) ? variantData : [];
    });

    if (!entityFromListCache) return cleanedEntityInstance;

    // Get entity schema to identify relation fields properly
    const entity = getEntityByName(entityName);
    if (!entity) return cleanedEntityInstance;

    // Identify relation fields from entity schema (not data structure)
    const relationFieldNames = new Set<string>();

    // Add relation fields from all relations (merged and normalized)
    const allRelations = getAllRelations(entityName);
    allRelations?.forEach((relation) => {
      relationFieldNames.add(relation.field_name);
    });

    // Start with fetched data (complete structure)
    const merged: Record<string, unknown> = { ...cleanedEntityInstance };

    // Merge scalar fields from cached data (updated values)
    // Relation fields are always kept from fetched data
    Object.keys(entityFromListCache).forEach((field) => {
      if (
        !relationFieldNames.has(field) &&
        (entityFromListCache as Record<string, unknown>)[field] !== undefined
      ) {
        // Special handling for variant fields - ensure they remain arrays
        if (field.endsWith("_variants")) {
          const cachedValue = (entityFromListCache as Record<string, unknown>)[field];
          const fetchedValue = merged[field];
          // Use cached value if it's a valid array, otherwise keep fetched value
          merged[field] = Array.isArray(cachedValue) ? cachedValue : fetchedValue;
        } else {
          merged[field] = (entityFromListCache as Record<string, unknown>)[field];
        }
      }
    });

    return merged;
  }, [
    entityInstance?.data,
    removeSystemFields,
    entityFromListCache,
    getEntityByName,
    entityName,
    getAllRelations,
  ]);

  useEffect(() => {
    if (isOpen) {
      // In create mode the instance query still yields an (empty) object, so check isCreate first.
      if (isCreate) {
        if (initialValues) setEntityInstanceValues({ ...initialValues });
        if (initialRelations) setItemsToSet({ ...initialRelations });
      } else if (mergedEntityInstance) {
        setEntityInstanceValues(mergedEntityInstance);
        // Keep the original data so the dirty check can compare against it on save.
        initialValuesRef.current = mergedEntityInstance;
      }
    } else {
      // Clean up state when closing
      setItemsToSet({});
      setItemsToConnect({});
      setItemsToDisconnect({});
      setEntityInstanceValues({});
      setShouldValidate(false);
      initialValuesRef.current = {};
    }
  }, [mergedEntityInstance, isOpen, isCreate, initialValues, initialRelations]);

  const handleUpdateOrCreateEntityInstance = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!shouldValidate) {
      setShouldValidate(true);
      if (!validateAllFields()) {
        return;
      }
    }

    if (!isFormValid) {
      return;
    }

    if (!entityInstanceValues) {
      return;
    }

    // Remove system fields before saving (integer_id, created_at, updated_at)
    const data = removeSystemFields(entityInstanceValues);
    const connectKeys = Object.keys(itemsToConnect);
    for (const key of connectKeys) {
      if (itemsToConnect[key].length > 0) {
        data[key + "_connect"] = itemsToConnect[key];
      }
    }

    const disconnectKeys = Object.keys(itemsToDisconnect);
    for (const key of disconnectKeys) {
      if (itemsToDisconnect[key].length > 0) {
        data[key + "_disconnect"] = itemsToDisconnect[key];
      }
    }

    for (const key of Object.keys(itemsToSet)) {
      // A to-one relation is typed `<Entity>Input` (GraphQLService only emits the list
      // form, and `_disconnect` at all, for to-many), so clearing it means an empty
      // input object — the backend reads `_set` with no ids as "unbind everything".
      data[key + "_set"] = itemsToSet[key] ?? {};
    }

    try {
      // Build fieldName -> DataType map once
      const entity = getEntityByName(entityName);
      const entityFields = entity?.entity_fields?.data ?? [];
      const fieldNameToType = new Map<string, DataTypeEnum>();
      for (const f of entityFields) {
        const name = f.name;
        if (name) fieldNameToType.set(name, f.data_type as DataTypeEnum);
      }

      const coerced = coerceEntityInstanceValues(data, fieldNameToType);

      // Dirty check: on update, only send fields that actually changed compared to
      // the originally-loaded values. Relation deltas (_connect/_disconnect/_set) are
      // already diffs and are always kept; `id` identifies the instance. Creates send
      // the full payload.
      let payload = coerced;
      const isUpdate = "id" in coerced && !!coerced.id;
      if (isUpdate) {
        const baseline = coerceEntityInstanceValues(initialValuesRef.current, fieldNameToType);
        payload = Object.fromEntries(
          Object.entries(coerced).filter(([key, value]) => {
            if (key === "id") return true;
            if (/_connect$|_disconnect$|_set$/.test(key)) return true;
            return !equal(value, baseline[key]);
          }),
        );
      }

      // This is done to not close the modal when the save had an error
      const maybePromise = onSave?.({
        dontTriggerFlows: !triggerFlows,
        data: payload,
      });
      // Await if a Promise was returned (React Query mutateAsync returns a Promise)
      if (maybePromise && typeof (maybePromise as Promise<unknown>).then === "function") {
        await (maybePromise as Promise<unknown>);
      }

      onOpenChanges?.(false);
    } catch (error) {
      console.error("[entity-instance-save] failed", error);
      toast.error("Failed to save. Please check your inputs.");
    }

    if (entityInstanceValues?.id) {
      capture("entity_instance_updated", {
        entity_name: entityName,
        entity_instance_id: entityInstanceValues.id,
        entity_instance_values: entityInstanceValues,
      });
    } else {
      capture("entity_instance_created", {
        entity_name: entityName,
        entity_instance_values: entityInstanceValues,
      });
    }
  };

  function coerceEntityInstanceValues(
    source: Record<string, unknown>,
    fieldNameToType: Map<string, DataTypeEnum>,
  ): Record<string, unknown> {
    const result: Record<string, unknown> = { ...source };
    for (const key of Object.keys(source)) {
      if (/_connect$|_disconnect$|_set$/.test(key)) continue;
      if (key.endsWith("_variants")) {
        const base = key.replace(/_variants$/, "");
        const type = fieldNameToType.get(base);
        if (!type) continue;
        const variants = source[key] as Array<{
          variant: string;
          value: string | null;
        }>;
        result[key] = variants?.map((v) => ({
          variant: v.variant,
          value: v.value == null ? null : (parseValue(String(v.value), type) as unknown),
        }));
        continue;
      }
      const type = fieldNameToType.get(key);
      if (!type) continue;
      const raw = source[key];
      if (typeof raw === "string" || raw == null) {
        result[key] = raw == null ? null : (parseValue(String(raw), type) as unknown);
      }
    }
    return result;
  }

  const getScalarValue = useCallback(
    (field: EntityField | EntityRelated) => {
      const fieldName = getFieldName(field);
      const isLocalized = isLocalizedField(field);

      if (isLocalized && !!selectedLanguage?.locale) {
        const fieldVariants =
          (entityInstanceValues?.[`${fieldName}_variants`] as Array<{
            variant: string;
            value: string | null;
          }>) ?? [];

        const variant = fieldVariants.find((x) => x.variant === selectedLanguage.locale);
        return (variant?.value as string) ?? "";
      }

      return (entityInstanceValues?.[fieldName] as string) ?? "";
    },
    [selectedLanguage, entityInstanceValues],
  );

  // a typed number would not advance the counter, so creates leave it to the range
  const isReadOnlySequence = (field: EntityField | EntityRelated) =>
    "data_type" in field &&
    field.data_type === "SEQUENCE" &&
    (isCreate ||
      (!!field.entity_sequence?.data?.locked && !!initialValuesRef.current[getFieldName(field)]));

  const setScalarValue = (field: EntityField | EntityRelated, value: string) => {
    const fieldName = getFieldName(field);
    const isLocalized = isLocalizedField(field);

    setEntityInstanceValues((pre) => {
      const newValueObject = { ...pre };

      if (isLocalized) {
        const key = `${fieldName}_variants`;

        let fieldVariants =
          (entityInstanceValues?.[key] as Array<{
            variant: string;
            value: string | null;
          }>) ?? [];

        // find the variant for the selected language
        let variant = fieldVariants.find((x) => x.variant === selectedLanguage?.locale);

        // if no variant is found, create a new one; probably it is null as it was not set before
        if (!variant && selectedLanguage?.locale) {
          variant = {
            variant: selectedLanguage?.locale,
            value,
          };
        }

        if (variant) {
          // set the variant value
          variant.value = value;
          // add the variant to the field variants
          fieldVariants = [
            // remove the variant for the selected language
            ...fieldVariants.filter((x) => x.variant !== selectedLanguage?.locale),
            // add the variant for the selected language
            variant,
          ];
          newValueObject[key] = fieldVariants;
        }
        return newValueObject;
      }

      const type = getFieldType(field) as DataTypeEnum;
      if (checkValidity(value, type)) {
        newValueObject[fieldName] = value;
      }
      return newValueObject;
    });
  };

  // Helper functions to map EntityField/EntityRelated to expected properties
  const getFieldName = (field: EntityField | EntityRelated): string => {
    return "field_name" in field ? field.field_name : field.name;
  };

  const getFieldType = (field: EntityField | EntityRelated): string => {
    if ("data_type" in field) {
      return field.data_type;
    } else {
      // For relations, return the GraphQL type format expected by components
      const entityName = field.target_entity.data.name;
      const isList = field.type.endsWith("Many");
      return isList ? `${entityName}ListData` : `${entityName}Data`;
    }
  };

  const isRelationField = (field: EntityField | EntityRelated): boolean => {
    return "entity" in field;
  };

  const isListField = (field: EntityField | EntityRelated): boolean => {
    return "type" in field && field.type.endsWith("Many");
  };

  const isLocalizedField = (field: EntityField | EntityRelated): boolean => {
    return "variant_entity_field" in field && !!field.variant_entity_field;
  };

  // Validate all fields of the entity, not just rendered ones
  const validateAllFields = useCallback(() => {
    const entity = getEntityByName(entityName);
    if (!entity) {
      return true;
    }

    const allFields = [
      ...(primaryEntity?.entity_fields?.data ?? []),
      ...(getAllRelations(entityName) ?? []),
    ];

    const newFieldValidity: Record<string, boolean> = {};

    allFields.forEach((field) => {
      const fieldName = getFieldName(field);
      if (
        !fieldName ||
        hiddenFields.has(fieldName) ||
        getFieldType(field) === "FieldVariantInput"
      ) {
        return;
      }

      if (isRelationField(field)) {
        // For relation fields, assume valid for now
        newFieldValidity[fieldName] = true;
        return;
      }

      // Scalar field: validate using validation_string when available
      const validationString =
        "data_type" in field ? (field as EntityField).validation_string : undefined;
      if (!validationString) {
        newFieldValidity[fieldName] = true;
        return;
      }

      const fieldValue = getScalarValue(field);
      const { isValid } = validateField(String(fieldValue ?? ""), validationString);
      newFieldValidity[fieldName] = isValid;
    });

    // Only update state if something actually changed to avoid render loops
    let changed = false;
    setFieldValidity((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(newFieldValidity);
      if (prevKeys.length !== nextKeys.length) {
        changed = true;
        return newFieldValidity;
      }
      for (const key of nextKeys) {
        if (prev[key] !== newFieldValidity[key]) {
          changed = true;
          break;
        }
      }
      return changed ? newFieldValidity : prev;
    });

    return Object.values(newFieldValidity).every(Boolean);
  }, [
    getEntityByName,
    entityName,
    primaryEntity?.entity_fields?.data,
    getAllRelations,
    hiddenFields,
    getScalarValue,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      validateAllFields();
    }
  }, [shouldValidate, validateAllFields, entityInstanceValues]);

  interface FieldWithMeta {
    fieldFromEntityList: EntityField | EntityRelated;
    fieldFromSectionConfig: Field;
  }

  const { getComponentMapping, getFieldsToFetch } = useEntityViewComponents();

  // FIXME: need to find out why this is getting recaclulated on every key stroke in an input field
  const sectionsToDisplay = useMemo<
    Array<{
      sectionName: string;
      fields: FieldWithMeta[];
    }>
  >(() => {
    const entity = getEntityByName(entityName);

    if (!entity) {
      throw new Error(`Entity ${entityName} not found`);
    }

    if (activeView?.section_config) {
      return activeView.section_config.map((section) => ({
        sectionName: section.name,
        fields: section.fields
          .map((f) => {
            const fieldOrRelation = getFieldOrRelationByPath(`${entityName}.${f.name}`);
            if (!fieldOrRelation) {
              return null;
            }

            const isRelation = "entity" in fieldOrRelation;

            // Find the relation field to display in case we need it
            if (isRelation && !f.relation_field_to_display) {
              const targetEntity = getEntityByName(fieldOrRelation.target_entity.data.name);
              const ignoredFields = ["id", "integer_id", "created_at", "updated_at"];
              const firstDisplayableField = targetEntity?.entity_fields?.data?.find(
                (field) => !ignoredFields.includes(field.name),
              );
              f.relation_field_to_display = firstDisplayableField?.name ?? "integer_id";
            }

            const mapping = isRelation
              ? getComponentMapping({
                  entityName: cleanupEntityName(fieldOrRelation.target_entity.data?.name ?? ""),
                })
              : undefined;

            let fieldsToFetch: Array<string> = [];

            if (isRelation) {
              fieldsToFetch =
                getFieldsToFetch({
                  entityName: cleanupEntityName(fieldOrRelation.target_entity.data?.name ?? ""),
                }) ?? [];
            }

            if (mapping) {
              f.mapping = {
                component: mapping.component,
                id: mapping.id!,
                config: toComponentTemplates(mapping.config),
                fieldsToFetch: fieldsToFetch,
              };
            }

            return {
              fieldFromEntityList: fieldOrRelation,
              fieldFromSectionConfig: f,
            };
          })
          .filter((x) => x !== null),
      }));
    }

    return [
      {
        sectionName: "",
        fields: [],
      },
    ];
  }, [
    entityName,
    activeView?.section_config,
    getEntityByName,
    getFieldOrRelationByPath,
    getComponentMapping,
    getFieldsToFetch,
  ]);

  const sectionsWithValues = useMemo(
    () =>
      sectionsToDisplay.map((section, idx) => ({
        ...section,
        value:
          section.sectionName && section.sectionName.length > 0
            ? section.sectionName
            : `section-${idx}`,
        label:
          section.sectionName && section.sectionName.length > 0 ? section.sectionName : "General",
      })),
    [sectionsToDisplay],
  );

  // Logic to determine which sections should be visible in tabs vs dropdown
  const visibleSections = useMemo(() => {
    if (!sectionsWithValues || sectionsWithValues.length <= 5) {
      return sectionsWithValues;
    }

    const activeIndex = sectionsWithValues.findIndex((s) => s.value === activeSectionTab);

    if (activeIndex === -1) {
      // If no active tab, show first 5
      return sectionsWithValues.slice(0, 5);
    }

    if (activeIndex < 4) {
      // If active tab is in first 4, show first 5
      return sectionsWithValues.slice(0, 5);
    }

    // If active tab is beyond first 4, always show first 4 + active tab
    // This ensures stable ordering: [1, 2, 3, 4, active]
    const firstFour = sectionsWithValues.slice(0, 4);
    const activeSection = sectionsWithValues[activeIndex];
    return [...firstFour, activeSection];
  }, [sectionsWithValues, activeSectionTab]);

  // Sections that should be in the dropdown
  const dropdownSections = useMemo(() => {
    if (!sectionsWithValues || sectionsWithValues.length <= 5) {
      return [];
    }
    return sectionsWithValues.filter((section) => !visibleSections.includes(section));
  }, [sectionsWithValues, visibleSections]);

  useEffect(() => {
    if (
      (!activeSectionTab || !sectionsWithValues.some((s) => s.value === activeSectionTab)) &&
      sectionsWithValues.length > 0
    ) {
      setActiveSectionTab(sectionsWithValues[0].value);
    }
  }, [sectionsWithValues, activeSectionTab]);

  const getShowEntitySelectBase = useCallback(
    (field: {
      fieldFromEntityList: EntityField | EntityRelated;
      fieldFromSectionConfig: Field;
    }) => {
      // Show for relation fields, but hide for PyloUser and PyloMedia (have specialized pickers)
      if ("entity" in field.fieldFromEntityList) {
        const targetEntityName = (field.fieldFromEntityList as EntityRelated).target_entity?.data
          ?.name;
        const targetEntityId = (field.fieldFromEntityList as EntityRelated).target_entity?.data?.id;
        const hasReadPermission = targetEntityId
          ? (entityPermissionsMap?.get(targetEntityId)?.has_read ?? false)
          : false;
        return (
          targetEntityName !== "PyloUser" && targetEntityName !== "PyloMedia" && hasReadPermission
        );
      }
      return false;
    },
    [entityPermissionsMap],
  );

  const renderFormField = (
    field: {
      fieldFromEntityList: EntityField | EntityRelated;
      fieldFromSectionConfig: Field;
    },
    idx: number,
  ) => {
    const fieldName = getFieldName(field.fieldFromEntityList);
    const isScalar = !isRelationField(field.fieldFromEntityList);
    // Per-field gating only applies to scalar/variant fields (what `field_paths` whitelists for the
    // base entity); relation editing goes through a separate mechanism and is left untouched here.
    const fieldReadable = isScalar ? canReadField(fieldName) : true;
    const fieldWritable = isScalar ? canWriteField(fieldName) : true;
    const isPermissionLocked = !fieldReadable || !fieldWritable;

    return (
      <div
        key={idx}
        style={{
          gridColumn: `span ${field.fieldFromSectionConfig.width || 2}`,
          gridRow: `span ${field.fieldFromSectionConfig.height || 1}`,
        }}
        className="flex flex-col pb-5"
      >
        <div className="flex justify-between gap-2 pb-1">
          <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Label
                  className="mb-0 block truncate text-base font-semibold"
                  htmlFor={getFieldName(field.fieldFromEntityList)}
                >
                  {getFieldName(field.fieldFromEntityList)}
                </Label>
              </TooltipTrigger>
              <TooltipContent>{getFieldName(field.fieldFromEntityList)}</TooltipContent>
            </Tooltip>
            {getShowEntitySelectBase(field) && (
              <div className="">
                <EntityFieldSelectBase
                  entityType={getFieldType(field.fieldFromEntityList)}
                  initialField={
                    eilStates[(field.fieldFromEntityList as EntityRelated).target_entity.data.name]
                      ?.field || field.fieldFromSectionConfig.relation_field_to_display
                  }
                  onFieldChange={(newField) => {
                    // Update the field's display in the current form session
                    updateEilState(
                      (field.fieldFromEntityList as EntityRelated).target_entity.data.name,
                      { field: newField },
                    );
                  }}
                />
              </div>
            )}
          </div>
          {isLocalizedField(field.fieldFromEntityList) && (
            <span className="flex items-center gap-0.5 text-xs text-secondary">
              <Globe className="h-3 w-3" />
              {selectedLanguage?.locale?.toLocaleUpperCase()}
            </span>
          )}
          {isScalar && !fieldReadable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex shrink-0 items-center text-gray-400">
                  <EyeOff className="h-3.5 w-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent>You don&apos;t have permission to view this field</TooltipContent>
            </Tooltip>
          )}
          {isScalar && fieldReadable && !fieldWritable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex shrink-0 items-center text-gray-400">
                  <Lock className="h-3.5 w-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent>You don&apos;t have permission to edit this field</TooltipContent>
            </Tooltip>
          )}
        </div>
        {!isRelationField(field.fieldFromEntityList) ? (
          <>
            <InputByFieldType
              name={getFieldName(field.fieldFromEntityList)}
              fieldType={getFieldType(field.fieldFromEntityList) as DataTypeEnum}
              formType={
                "form_type" in field.fieldFromEntityList
                  ? field.fieldFromEntityList.form_type
                  : undefined
              }
              formatCurrency={
                "format_currency" in field.fieldFromEntityList
                  ? (field.fieldFromEntityList as EntityField).format_currency
                  : undefined
              }
              formatLocale={
                "format_locale" in field.fieldFromEntityList
                  ? (field.fieldFromEntityList as EntityField).format_locale
                  : undefined
              }
              enumValues={
                "data_type" in field.fieldFromEntityList &&
                (field.fieldFromEntityList as EntityField).data_type === "ENUM"
                  ? (field.fieldFromEntityList as EntityField).entity_field_enum_values?.data
                  : undefined
              }
              validationString={
                "data_type" in field.fieldFromEntityList
                  ? (field.fieldFromEntityList as EntityField).validation_string
                  : undefined
              }
              showValidationErrors={shouldValidate && !isPermissionLocked}
              value={getScalarValue(field.fieldFromEntityList)}
              onChange={(e) => {
                setScalarValue(field.fieldFromEntityList, e.target.value);
              }}
              disabled={
                systemFields.includes(fieldName) ||
                isPermissionLocked ||
                isReadOnlySequence(field.fieldFromEntityList)
              }
              // A permission-locked field can't be fixed by the user, so never let its validity
              // block the save.
              onValidityChange={(name, valid) =>
                updateFieldValidity(name, isPermissionLocked ? true : valid)
              }
            />
          </>
        ) : getFieldType(field.fieldFromEntityList) === "PyloMediaData" ||
          getFieldType(field.fieldFromEntityList) === "PyloMediaListData" ? (
          <div className="w-full">
            <Dropzone
              fieldName={getFieldName(field.fieldFromEntityList)}
              isList={isListField(field.fieldFromEntityList)}
              parentEntityName={entityName}
              parentEntityId={entityInstanceId}
              itemsToConnect={itemsToConnect}
              itemsToDisconnect={itemsToDisconnect}
              itemsToSet={itemsToSet}
              setItemsToConnect={setItemsToConnect}
              setItemsToDisconnect={setItemsToDisconnect}
              setItemsToSet={setItemsToSet}
              accept={buildFileAcceptConfig({
                media_allowed_computed_mime_types: (field.fieldFromEntityList as EntityRelated)
                  .media_allowed_computed_mime_types,
                media_allowed_computed_extensions: (field.fieldFromEntityList as EntityRelated)
                  .media_allowed_computed_extentions,
              })}
              allowedMimeTypes={
                (field.fieldFromEntityList as EntityRelated).media_allowed_computed_mime_types
              }
            />
          </div>
        ) : (
          <>
            {/* The relation connect / disconnect component fetches its own data with the provided
          parent entity_name and parent_entity_id */}
            <ConnectOrDisconnectEntityRelation
              key={`${getFieldName(field.fieldFromEntityList)}-${entityInstanceId || "create"}`}
              fieldName={getFieldName(field.fieldFromEntityList)}
              entityType={getFieldType(field.fieldFromEntityList)}
              isList={isListField(field.fieldFromEntityList)}
              parentEntityName={entityName}
              parentEntityId={entityInstanceId}
              itemsToConnect={itemsToConnect}
              itemsToDisconnect={itemsToDisconnect}
              itemsToSet={itemsToSet}
              setItemsToConnect={setItemsToConnect}
              setItemsToDisconnect={setItemsToDisconnect}
              setItemsToSet={setItemsToSet}
              cachedValue={
                entityInstanceValues?.[getFieldName(field.fieldFromEntityList)] as OptionType
              }
              relationFieldToDisplay={
                eilStates[(field.fieldFromEntityList as EntityRelated).target_entity.data.name]
                  ?.field || field.fieldFromSectionConfig.relation_field_to_display
              }
              fieldsToFetch={field.fieldFromSectionConfig.mapping?.fieldsToFetch}
              mapping={field.fieldFromSectionConfig.mapping as EntityViewComponentMapping}
              isTightlyCoupled={
                (field.fieldFromEntityList as EntityRelated).target_entity_is_tightly_coupled
              }
            />
          </>
        )}
      </div>
    );
  };

  // A permalink can name a record that was deleted, never existed, or belongs to another
  // organization. Without this the empty form reads as an editable record.
  if (entityInstanceId && isEntityInstanceError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-sm text-gray-600">
          This record could not be loaded. It may have been deleted, or you may not have access to
          it.
        </p>
        <Button type="button" variant="outline" onClick={() => onOpenChanges?.(false)}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleUpdateOrCreateEntityInstance} className="flex h-full flex-col">
      <div className="flex min-h-0 w-full grow flex-col">
        <StackedSheetHeader className="p-6 pb-4">
          <StackedSheetTitle asChild>
            <div className="flex w-full flex-col gap-2">
              <div className="flex flex-row items-center justify-between">
                <Title>
                  {isCreate ? "Create" : "Update"} {entityName}
                </Title>
                <div className="absolute right-12 top-4">
                  {entityInstanceId && (
                    <EntityInstanceHistoryBadge
                      updatedAt={
                        // @ts-expect-error because of the generalization
                        entityInstance?.data?.updated_at
                      }
                      onClick={openActivityFeed}
                    />
                  )}
                </div>
              </div>
              {!!entityInstanceId && (
                <div className="flex flex-row items-center gap-2 text-gray-400">
                  <span
                    className="cursor-pointer text-xs font-normal"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        // @ts-expect-error because of the generalization
                        entityInstance?.data?.integer_id ?? "",
                      );
                      toast.success("id copied to clipboard");
                    }}
                    title="Click to copy"
                  >
                    {/* @ts-expect-error because of the generalization */}
                    id: {entityInstance?.data?.integer_id}
                  </span>
                  <span
                    className="cursor-pointer text-xs font-normal"
                    onClick={() => {
                      navigator.clipboard.writeText(entityInstanceId);
                      toast.success("id copied to clipboard");
                    }}
                    title="Click to copy"
                  >
                    #{entityInstanceId}
                  </span>
                  {isFetchingEntityInstance && (
                    <span>
                      <LoaderCircle className="animate-spin" size={12} />
                    </span>
                  )}
                </div>
              )}

              <div className="flex w-full flex-row items-center justify-between">
                {!!availableViews?.length && availableViews.length > 1 && (
                  <div className="flex h-8 flex-row gap-2 text-gray-600">
                    <Select
                      value={selectedViewId || activeView?.id}
                      onValueChange={setSelectedViewId}
                    >
                      <SelectTrigger className="h-8 w-[180px] ring-0 focus:ring-0 active:ring-0">
                        <SelectValue placeholder="Select view" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableViews.map((view) => (
                          <SelectItem key={view.id} value={view.id}>
                            {view.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="ml-auto">
                  {sectionsToDisplay?.some((x) =>
                    x.fields.some((f) => isLocalizedField(f.fieldFromEntityList)),
                  ) && <EntityInstanceLanguageSelect />}
                </div>
              </div>
              {sectionsWithValues.length > 1 && (
                <div className="mt-2">
                  <div className="flex items-center gap-1">
                    {visibleSections.map((section) => (
                      <button
                        key={section.value}
                        className={cn(
                          "flex h-8 min-w-[80px] cursor-pointer items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm transition-colors",
                          {
                            "bg-gray-100 text-gray-900": section.value === activeSectionTab,
                            "text-gray-600 hover:bg-gray-50 hover:text-gray-900":
                              section.value !== activeSectionTab,
                          },
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveSectionTab(section.value);
                        }}
                      >
                        {section.label}
                      </button>
                    ))}
                    {dropdownSections.length > 0 && (
                      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                        <DropdownMenuTrigger asChild>
                          <button className="flex h-8 items-center rounded-md px-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-900">
                            +{dropdownSections.length}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" side="bottom" className="min-w-[220px]">
                          <DropdownMenuItem disabled className="font-bold text-black">
                            Additional Sections
                          </DropdownMenuItem>
                          {dropdownSections.map((section) => (
                            <DropdownMenuItem
                              key={section.value}
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSectionTab(section.value);
                                setIsDropdownOpen(false);
                              }}
                              className={cn("flex items-center gap-2", {
                                "font-semibold": section.value === activeSectionTab,
                              })}
                            >
                              {section.value === activeSectionTab && (
                                <Check className="h-4 w-4 text-black" />
                              )}
                              <span className="ml-1 flex-1">{section.label}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              )}
            </div>
          </StackedSheetTitle>
          <StackedSheetDescription
            className="h-0 p-0"
            aria-describedby={`update ${entityName}`}
          ></StackedSheetDescription>
        </StackedSheetHeader>
        <div className="border-b border-gray-200" />

        <div
          ref={formScrollRef}
          className="flex min-h-0 flex-1 grow flex-col overflow-y-auto p-6 py-4"
        >
          <div className="px-1">
            {entityName !== "PyloMedia" && (
              <div className="h-full min-h-0 grow overflow-y-auto pt-4">
                {sectionsWithValues.map((section) => (
                  <div
                    key={section.value}
                    className={cn(
                      "flex flex-col rounded-md bg-white",
                      section.value === activeSectionTab ? "block" : "hidden",
                    )}
                  >
                    <div className="grid w-full grid-cols-3 gap-4">
                      {section.fields.length === 0 && (
                        <p className="col-span-3 w-full text-center text-muted-foreground">
                          Empty section.
                        </p>
                      )}
                      {/* render the form fields */}
                      {section.fields.map((field, fieldIdx) =>
                        renderFormField(
                          {
                            fieldFromEntityList: field.fieldFromEntityList,
                            fieldFromSectionConfig: field.fieldFromSectionConfig,
                          },
                          fieldIdx,
                        ),
                      )}
                    </div>
                  </div>
                ))}
                {entityInstanceId && (
                  <div className="mt-6">
                    <EntityInstanceActivityFeed
                      entityName={entityName}
                      entityInstanceId={entityInstanceId}
                      anchorId={activityAnchorId}
                      expanded={activityExpanded}
                      onToggle={() => setActivityExpanded((v) => !v)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200" />
      {shouldValidate &&
        !isFormValid &&
        (() => {
          const invalidFields = Object.keys(fieldValidity).filter((key) => !fieldValidity[key]);
          const invalidFieldsLength = invalidFields.length;

          // Check if we're in the "all fields view" (no section_config or empty sectionName)
          const isAllFieldsView =
            !activeView?.section_config ||
            (sectionsWithValues.length === 1 && sectionsWithValues[0].sectionName === "");

          // Get current section's field names
          const currentSection = sectionsWithValues.find((s) => s.value === activeSectionTab);
          const currentSectionFieldNames =
            currentSection?.fields.map((f) => getFieldName(f.fieldFromEntityList)) || [];

          // Check if any invalid fields are NOT in the current section
          const invalidFieldsNotInCurrentSection = invalidFields.filter(
            (fieldName) => !currentSectionFieldNames.includes(fieldName),
          );

          const hasInvalidFieldsOutsideCurrentSection = invalidFieldsNotInCurrentSection.length > 0;

          return (
            <div className="flex items-center gap-1 px-6 pt-3 text-xs text-gray-500">
              <CircleAlert className="h-3 w-3 text-yellow-500" />
              {invalidFieldsLength} invalid field
              {invalidFieldsLength > 1 ? "s" : ""}
              {!isAllFieldsView && hasInvalidFieldsOutsideCurrentSection && (
                <>
                  , {invalidFieldsNotInCurrentSection.length} of them not in this view -{" "}
                  <button
                    type="button"
                    className="text-blue-600 underline hover:text-blue-800"
                    onClick={() => {
                      const defView = availableViews.find((v) => isDefaultView(v.id));
                      if (defView) setSelectedViewId(defView.id);
                    }}
                  >
                    fix in all fields view
                  </button>
                </>
              )}
            </div>
          );
        })()}
      {/* TODO: we also need to show this option when one of the related sub entities got updated and will trigger a flow */}
      {isBottomLevel ? (
        <FlowTriggerSwitch
          flows={relatedFlows}
          checked={triggerFlows}
          onCheckedChange={setTriggerFlows}
          className="px-6 pt-3"
        />
      ) : (
        <FlowTriggerHint flows={relatedFlows} className="px-6 pt-3" />
      )}
      <div className="p-6 py-3">
        <Button
          type="submit"
          variant="secondary"
          className="w-fit self-end"
          disabled={isCreate ? !permissions.has_create : !permissions.has_update}
        >
          Save
        </Button>
      </div>
    </form>
  );
};
